"use client";

import { createContext, useContext, useState, useEffect, useCallback, useTransition, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { Overlay, Center, Loader } from "@forestsync/ui";
import { setUiModeAction } from "@/actions/ui-mode";
import { UI_MODE_LS_KEY } from "@/lib/ui-mode-constants";
import type { UiMode } from "@forestsync/db";

interface UiModeContextValue {
  mode: UiMode;
  setMode: (mode: UiMode) => void;
  toggleMode: () => void;
  isTransitioning: boolean;
  /**
   * Sync local mode state without persisting to DB or triggering router.refresh().
   * Used by AuthProvider to correct a stale initialMode from a CDN-cached RSC.
   */
  syncMode: (mode: UiMode) => void;
}

const UiModeContext = createContext<UiModeContextValue | null>(null);

export function useUiMode(): UiModeContextValue {
  const ctx = useContext(UiModeContext);
  if (!ctx) {
    throw new Error("useUiMode must be used within a UiModeProvider");
  }
  return ctx;
}

export function UiModeProvider({ initialMode, children }: { initialMode: UiMode; children: ReactNode }) {
  const [mode, setModeState] = useState<UiMode>(initialMode);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  // On mount, sync localStorage to match the server-resolved initialMode.
  // The server already reconciled DB vs cookie — trust it as the source of truth.
  useEffect(() => {
    localStorage.setItem(UI_MODE_LS_KEY, initialMode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab sync via storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === UI_MODE_LS_KEY && (e.newValue === "standard" || e.newValue === "trader")) {
        setModeState(e.newValue)
      }
    }
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setMode = useCallback((next: UiMode) => {
    if (next !== "standard" && next !== "trader") return
    setModeState(next);
    localStorage.setItem(UI_MODE_LS_KEY, next);
    startTransition(async () => {
      await setUiModeAction(next)
      router.refresh()
    })
  }, [router]);

  const syncMode = useCallback((next: UiMode) => {
    if (next !== "standard" && next !== "trader") return
    setModeState(next)
    localStorage.setItem(UI_MODE_LS_KEY, next)
  }, [])

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: UiMode = prev === "standard" ? "trader" : "standard"
      localStorage.setItem(UI_MODE_LS_KEY, next);
      startTransition(async () => {
        await setUiModeAction(next)
        router.refresh()
      })
      return next;
    });
  }, [router]);

  return (
    <UiModeContext.Provider value={{ mode, setMode, toggleMode, isTransitioning: isPending, syncMode }}>
      {isPending && (
        <Overlay fixed backgroundOpacity={0.35} zIndex={1000}>
          <Center h="100vh">
            <Loader size="lg" />
          </Center>
        </Overlay>
      )}
      {children}
    </UiModeContext.Provider>
  );
}
